import {useAuth} from "./../../../contexts/AuthContext"
import React, {useEffect, useState} from "react";
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useNavigate, useMatch} from "react-router-dom";
import {getConversations, getConversation, postConversation, postMessage, deleteConversation, postMessageToAsana} from "../../../api/conversations";
import {getTenantsWithClients} from "../../../api/accounts";
import {getAsanaDocuments, refreshAsana} from "../../../api/documents";
import {Input, Select, TextArea} from "../../elements/forms/Inputs";
import {PrimaryButton, SecondaryButton} from "../../elements/Buttons";
import {PlusIcon, ChatBubbleLeftRightIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon} from "@heroicons/react/24/outline";
import {classNames} from "../../../utils/misc";
import {formatDateTime} from "../../../utils/formatters";
import ListItemLoader from "../../elements/ListItemLoader";
import {toast} from "react-toastify";
import {DeleteButton} from "../../elements/DeleteButton";
import {baseHandleChange} from "../../elements/forms/Form";
import {validate} from "../../../utils/validation";
import Joi from "joi";
import ReactMarkdown from 'react-markdown';


export default function ConversationList() {

    const {currentUser} = useAuth()
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    // Determine which route we're on
    const matchNew = useMatch('/chats/new')
    const matchId = useMatch('/chats/:conversationId')

    // Derive conversationId from the matched route
    const conversationId = matchNew ? 'new' : matchId?.params?.conversationId

    const [selectedConversationId, setSelectedConversationId] = useState(null)
    const [isCreatingNew, setIsCreatingNew] = useState(false)
    const [newMessage, setNewMessage] = useState("")
    const [optimisticMessages, setOptimisticMessages] = useState([])
    const [isWaitingForAssistant, setIsWaitingForAssistant] = useState(false)
    const [messageMode, setMessageMode] = useState('RETRIEVAL_ONLY')

    // Fetch conversations list
    const {data: conversations, isLoading: conversationsLoading} = useQuery(
        ["conversations", currentUser.id],
        getConversations,
        {refetchInterval: 1000 * 60 * 2} // 2 minutes
    )

    // Fetch selected conversation detail
    const {data: conversationDetail, isLoading: detailLoading} = useQuery(
        ["conversation", selectedConversationId],
        () => getConversation(selectedConversationId),
        {enabled: !!selectedConversationId && !isCreatingNew}
    )

    // Fetch tenants with clients for new conversation
    const {data: tenants} = useQuery(
        ['tenants_with_clients', currentUser.id],
        getTenantsWithClients,
        {refetchInterval: 1000 * 60 * 60 * 24}
    )

    // Sync URL parameter with selected conversation
    useEffect(() => {
        if (conversationId === 'new') {
            setIsCreatingNew(true)
            setSelectedConversationId(null)
            setNewMessage("")
            setOptimisticMessages([])
            setIsWaitingForAssistant(false)
            setMessageMode('RETRIEVAL_ONLY')
        } else if (conversationId) {
            setSelectedConversationId(parseInt(conversationId))
            setIsCreatingNew(false)
            setNewMessage("")
            setOptimisticMessages([])
            setIsWaitingForAssistant(false)
            setMessageMode('RETRIEVAL_ONLY')
        } else {
            setSelectedConversationId(null)
            setIsCreatingNew(false)
            setNewMessage("")
            setOptimisticMessages([])
            setIsWaitingForAssistant(false)
            setMessageMode('RETRIEVAL_ONLY')
        }
    }, [conversationId])

    function handleSelectConversation(id) {
        navigate(`/chats/${id}`)
    }

    function handleNewConversation() {
        navigate('/chats/new')
    }

    async function handleSendMessage() {
        if (!newMessage.trim()) return

        try {
            if (selectedConversationId) {
                const userMessageContent = newMessage

                // Optimistic UI update - add user message immediately
                const optimisticUserMessage = {
                    id: `temp-${Date.now()}`,
                    content: userMessageContent,
                    role: 'user',
                    created_at: new Date().toISOString(),
                    isOptimistic: true
                }

                setOptimisticMessages([optimisticUserMessage])
                setNewMessage("")
                setIsWaitingForAssistant(true)

                // Send message to backend
                await postMessage(selectedConversationId, {
                    content: userMessageContent,
                    role: 'user',
                    mode: messageMode
                })

                // Backend returns both user and assistant messages
                // Refetch to get the real messages
                await queryClient.invalidateQueries(["conversation", selectedConversationId])
                await queryClient.invalidateQueries(["conversations", currentUser.id])

                // Clear optimistic state
                setOptimisticMessages([])
                setIsWaitingForAssistant(false)
            }
        } catch (error) {
            toast.error("Failed to send message")
            setOptimisticMessages([])
            setIsWaitingForAssistant(false)
        }
    }

    async function handleDeleteConversation(id) {
        try {
            await deleteConversation(id)
            queryClient.invalidateQueries(["conversations", currentUser.id])
            if (selectedConversationId === id) {
                navigate('/chats')
            }
            toast.success("Conversation deleted")
        } catch (error) {
            toast.error("Failed to delete conversation")
        }
    }

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Sidebar */}
            <ConversationSidebar
                conversations={conversations}
                conversationsLoading={conversationsLoading}
                selectedConversationId={selectedConversationId}
                isCreatingNew={isCreatingNew}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                onDeleteConversation={handleDeleteConversation}
            />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col">
                {isCreatingNew ? (
                    <NewConversationForm
                        tenants={tenants}
                        currentUser={currentUser}
                        onCancel={() => navigate('/chats')}
                        onSuccess={(newConvId) => {
                            queryClient.invalidateQueries(["conversations", currentUser.id])
                            navigate(`/chats/${newConvId}`)
                        }}
                    />
                ) : selectedConversationId ? (
                    <>
                        <ConversationDetail
                            conversation={conversationDetail}
                            isLoading={detailLoading}
                            optimisticMessages={optimisticMessages}
                            isWaitingForAssistant={isWaitingForAssistant}
                        />
                        <ConversationInput
                            message={newMessage}
                            setMessage={setNewMessage}
                            onSend={handleSendMessage}
                            disabled={isWaitingForAssistant}
                            mode={messageMode}
                            setMode={setMessageMode}
                        />
                    </>
                ) : (
                    <EmptyState onNewConversation={handleNewConversation} />
                )}
            </div>
        </div>
    )
}

// Sidebar Component
function ConversationSidebar({conversations, conversationsLoading, selectedConversationId, isCreatingNew, onSelectConversation, onNewConversation, onDeleteConversation}) {
    return (
        <aside className="w-80 bg-gray-900 text-white flex flex-col border-r border-gray-700">
            {/* Header */}
            <div className="p-4 border-b border-gray-700">
                <button
                    onClick={onNewConversation}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span className="font-medium">New Conversation</span>
                </button>
            </div>

            {/* Conversations List */}
            <nav className="flex-1 overflow-y-auto p-2">
                {conversationsLoading ? (
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="p-3 bg-gray-800 rounded-lg animate-pulse h-16" />
                        ))}
                    </div>
                ) : conversations && conversations.length > 0 ? (
                    <ul className="space-y-1">
                        {conversations.map((conv) => (
                            <ConversationSidebarItem
                                key={conv.id}
                                conversation={conv}
                                isSelected={selectedConversationId === conv.id && !isCreatingNew}
                                onSelect={() => onSelectConversation(conv.id)}
                                onDelete={() => onDeleteConversation(conv.id)}
                            />
                        ))}
                    </ul>
                ) : (
                    <div className="text-center text-gray-400 mt-8">
                        <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No conversations yet</p>
                    </div>
                )}
            </nav>
        </aside>
    )
}

// Sidebar Item Component
function ConversationSidebarItem({conversation, isSelected, onSelect, onDelete}) {
    const [isDeleteHovered, setIsDeleteHovered] = useState(false)

    return (
        <li
            className={classNames(
                "group relative p-3 rounded-lg cursor-pointer transition-colors",
                isSelected ? "bg-gray-700" : "hover:bg-gray-800"
            )}
            onClick={onSelect}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate">
                        {conversation.title || "Untitled Conversation"}
                    </h3>
                    {conversation.client?.name && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                            {conversation.client.name}
                        </p>
                    )}
                    {conversation.last_message_at && (
                        <p className="text-xs text-gray-500 mt-1">
                            {formatDateTime(conversation.last_message_at)}
                        </p>
                    )}
                </div>
                <div
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                    onMouseEnter={() => setIsDeleteHovered(true)}
                    onMouseLeave={() => setIsDeleteHovered(false)}
                    className={classNames(
                        "transition-opacity",
                        isDeleteHovered ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    )}
                >
                    <DeleteButton
                        onDelete={onDelete}
                        iconOnly={true}
                        classNameIn="text-gray-400 hover:text-red-400"
                    />
                </div>
            </div>
        </li>
    )
}

// New Conversation Form Component
function NewConversationForm({tenants, currentUser, onCancel, onSuccess}) {
    const queryClient = useQueryClient()
    const [formData, setFormData] = useState({
        tenant: null,
        client: null,
        title: "",
        asanaDocument: null
    })
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)

    // Fetch Asana documents
    const {data: asanaDocuments, refetch: refetchAsanaDocuments} = useQuery(
        ['asana_documents', currentUser.id],
        getAsanaDocuments,
        {refetchInterval: 1000 * 60 * 5} // 5 minutes
    )

    // Preselect tenant if there's only one
    useEffect(() => {
        if (!formData.tenant && tenants && tenants.length === 1) {
            setFormData(prev => ({...prev, tenant: tenants[0].id}))
        }
    }, [tenants, formData.tenant])

    // Update tenant and client when Asana document is selected
    useEffect(() => {
        if (formData.asanaDocument && asanaDocuments) {
            const selectedDoc = asanaDocuments.find(doc => doc.id === formData.asanaDocument)
            if (selectedDoc) {
                setFormData(prev => ({
                    ...prev,
                    tenant: selectedDoc.tenant,
                    client: selectedDoc.client || null, // Full client object, not just ID
                    title: prev.title || selectedDoc.title // Only set title if it's empty
                }))
            }
        }
    }, [formData.asanaDocument, asanaDocuments])

    // Clear Asana document selection if client changes and selected doc no longer matches
    useEffect(() => {
        if (formData.asanaDocument && asanaDocuments) {
            const selectedDoc = asanaDocuments.find(doc => doc.id === formData.asanaDocument)
            if (selectedDoc) {
                // Check if selected doc matches current client filter
                const matchesClient = formData.client?.id
                    ? selectedDoc.client?.id === formData.client.id
                    : true
                const matchesTenant = formData.tenant
                    ? selectedDoc.tenant === formData.tenant
                    : true

                if (!matchesClient || !matchesTenant) {
                    setFormData(prev => ({...prev, asanaDocument: null}))
                }
            }
        }
    }, [formData.client?.id, formData.tenant])

    function handleChange(evt) {
        baseHandleChange(evt, formData, setFormData, errors, setErrors, schema)
    }

    async function handleRefreshAsana() {
        setIsRefreshing(true)
        try {
            await refreshAsana()
            await refetchAsanaDocuments()
            toast.success("Asana documents refreshed")
        } catch (error) {
            toast.error("Failed to refresh Asana documents")
        } finally {
            setIsRefreshing(false)
        }
    }

    const schema = {
        tenant: Joi.number().required().messages({"number.base": "Please select a team"}),
        title: Joi.string().required().messages({"string.empty": "Please enter a title"})
    }

    async function handleSubmit(e) {
        e.preventDefault()
        setIsSubmitting(true)

        // validation of the entire form/schema
        const validationErrors = validate(schema, formData)
        setErrors(validationErrors)
        if (validationErrors) {
            setErrors("Please check your inputs.")
            setIsSubmitting(false)
            return
        } else {
            setErrors("")
        }

        try {
            const newConv = await postConversation({
                tenant: formData.tenant,
                client: formData.client,
                title: formData.title,
                document: formData.asanaDocument
            })

            // If an Asana document is selected, add it as the first message
            if (formData.asanaDocument) {
                const selectedDoc = asanaDocuments.find(doc => doc.id === formData.asanaDocument)
                if (selectedDoc) {
                    // const messageContent = `${selectedDoc.processed_content}`
                    await postMessage(newConv.id, {
                        role: 'user',
                        content: '',
                        mode: 'ASANA'
                    })
                }
            }

            toast.success("Conversation created")
            onSuccess(newConv.id)
        } catch (error) {
            toast.error("Failed to create conversation")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="flex-1 flex flex-col bg-white">
            <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-xl font-semibold text-gray-900">New Conversation</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
                    <Select
                        path="tenant"
                        label="Team"
                        className="text-left"
                        options={tenants}
                        valueAttr="id"
                        labelAttr="name"
                        onChange={handleChange}
                        data={formData}
                        errors={errors}
                        isClearable={false}
                        valueOnly={true}
                    />

                    <Select
                        path="client"
                        label="Client (Optional)"
                        className="text-left"
                        options={formData.tenant ? tenants?.find(t => t.id === formData.tenant)?.clients || [] : []}
                        valueAttr="id"
                        labelAttr="name"
                        onChange={handleChange}
                        data={formData}
                        errors={errors}
                        isClearable={true}
                        valueOnly={false}
                    />

                    <Input
                        path="title"
                        type="text"
                        label="Conversation Title"
                        onChange={handleChange}
                        errors={errors}
                        data={formData}
                    />

                    {/* Asana Document Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Asana Ticket (Optional)
                        </label>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <Select
                                    path="asanaDocument"
                                    className="text-left"
                                    options={(asanaDocuments || []).filter(doc => {
                                        // If a client is selected, filter by that client
                                        if (formData.client?.id) {
                                            return doc.client?.id === formData.client.id
                                        }
                                        // If only tenant is selected, filter by tenant
                                        if (formData.tenant) {
                                            return doc.tenant === formData.tenant
                                        }
                                        // Otherwise show all
                                        return true
                                    })}
                                    valueAttr="id"
                                    labelAttr="title"
                                    onChange={handleChange}
                                    data={formData}
                                    errors={errors}
                                    isClearable={true}
                                    valueOnly={true}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleRefreshAsana}
                                disabled={isRefreshing}
                                className={classNames(
                                    "inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md",
                                    isRefreshing
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                )}
                            >
                                <ArrowPathIcon className={classNames("h-5 w-5", isRefreshing && "animate-spin")} />
                                <span className="ml-2">{isRefreshing ? "Refreshing..." : "Refresh"}</span>
                            </button>
                        </div>
                        {formData.asanaDocument && asanaDocuments && (
                            <p className="mt-2 text-sm text-gray-500">
                                Selected ticket will auto-populate team, client, and title. The ticket will be added as the first message with problem-solving mode.
                            </p>
                        )}
                    </div>

                    <div className="flex gap-3">
                        <PrimaryButton
                            label={isSubmitting ? "Creating..." : "Create Conversation"}
                            disabled={isSubmitting}
                            onClick={handleSubmit}
                        />
                        <SecondaryButton
                            label="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </div>
    )
}

// Conversation Detail Component
function ConversationDetail({conversation, isLoading, optimisticMessages, isWaitingForAssistant}) {
    if (isLoading) {
        return (
            <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-4xl mx-auto space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                            <div className="h-20 bg-gray-200 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!conversation || !conversation.messages) {
        return (
            <div className="flex-1 flex items-center justify-center text-gray-500">
                <p>No messages yet</p>
            </div>
        )
    }

    // Combine real messages with optimistic messages
    const allMessages = [...conversation.messages, ...optimisticMessages]

    return (
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="max-w-4xl mx-auto space-y-4">
                {/* Conversation Header */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-semibold text-gray-900">{conversation.title}</h2>
                                {conversation.document_source_url && (
                                    <a
                                        href={conversation.document_source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800"
                                        title="Open source document"
                                    >
                                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                                    </a>
                                )}
                            </div>
                            {conversation.client.name && (
                                <p className="text-sm text-gray-600 mt-1">{conversation.client.name}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Messages */}
                {allMessages.map((message) => (
                    <MessageBubble key={message.id} message={message} conversation={conversation} />
                ))}

                {/* Assistant Loading Indicator */}
                {isWaitingForAssistant && (
                    <AssistantLoadingBubble />
                )}
            </div>
        </div>
    )
}

// Message Bubble Component
function MessageBubble({message, conversation}) {
    const isUser = message.role === 'user'
    const isOptimistic = message.isOptimistic
    const hasSources = !isUser && message.sources && message.sources.length > 0
    const hasDocument = conversation?.document

    return (
        <div className={classNames(
            "flex",
            isUser ? "justify-end" : "justify-start"
        )}>
            <div className={classNames(
                "max-w-3xl rounded-lg px-4 py-3 shadow-sm",
                isUser
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-900",
                isOptimistic && "opacity-70"
            )}>
                <div className="flex items-center gap-2 mb-1">
                    <span className={classNames(
                        "text-xs font-medium",
                        isUser ? "text-indigo-100" : "text-gray-500"
                    )}>
                        {isUser ? "You" : "Assistant"}
                    </span>
                    {message.created_at && (
                        <span className={classNames(
                            "text-xs",
                            isUser ? "text-indigo-200" : "text-gray-400"
                        )}>
                            {formatDateTime(message.created_at)}
                        </span>
                    )}
                </div>
                <div className="text-sm">
                    {isUser ? (
                        <div className="whitespace-pre-wrap">{message.content}</div>
                    ) : (
                        <div className="prose prose-sm max-w-none">
                            <ReactMarkdown
                                components={{
                                    // Customize markdown rendering
                                    p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2" {...props} />,
                                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2" {...props} />,
                                    li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                    h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2 mt-3" {...props} />,
                                    h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2 mt-3" {...props} />,
                                    h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-2 mt-2" {...props} />,
                                    code: ({node, inline, ...props}) =>
                                        inline
                                            ? <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs" {...props} />
                                            : <code className="block bg-gray-100 text-gray-800 p-2 rounded text-xs overflow-x-auto mb-2" {...props} />,
                                    strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
                                    em: ({node, ...props}) => <em className="italic" {...props} />,
                                    a: ({node, ...props}) => <a className="text-indigo-600 hover:text-indigo-800 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                                }}
                            >
                                {message.content}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>

                {/* Sources Section and Asana Button */}
                {(hasSources || (!isUser && hasDocument)) && (
                    <MessageSourcesAndActions
                        sources={message.sources}
                        messageId={message.id}
                        hasDocument={hasDocument}
                        isOptimistic={isOptimistic}
                    />
                )}
            </div>
        </div>
    )
}

// Message Sources and Actions Component
function MessageSourcesAndActions({sources, messageId, hasDocument, isOptimistic}) {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const hasSources = sources && sources.length > 0

    async function handleSendToAsana() {
        if (isOptimistic) return // Don't allow sending optimistic messages

        setIsSending(true)
        try {
            await postMessageToAsana(messageId)
            toast.success("Reply sent to Asana")
        } catch (error) {
            toast.error("Failed to send reply to Asana")
        } finally {
            setIsSending(false)
        }
    }

    return (
        <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
                {hasSources && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <span>Sources ({sources.length})</span>
                        <svg
                            className={classNames(
                                "w-4 h-4 transition-transform",
                                isExpanded && "rotate-180"
                            )}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                )}

                {hasDocument && !isOptimistic && (
                    <button
                        onClick={handleSendToAsana}
                        disabled={isSending}
                        className={classNames(
                            "text-xs font-medium transition-colors",
                            isSending
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-indigo-600 hover:text-indigo-800"
                        )}
                    >
                        {isSending ? "Sending..." : "Send this reply to Asana"}
                    </button>
                )}
            </div>

            {hasSources && isExpanded && (
                <ul className="mt-2 space-y-2">
                    {sources.map((source, index) => (
                        <SourceItem key={index} source={source} index={index} />
                    ))}
                </ul>
            )}
        </div>
    )
}

// Individual Source Item Component
function SourceItem({source, index}) {
    const metadata = source.metadata || {}
    const title = metadata.title || `Source ${index + 1}`
    const sourceType = source.source_type || metadata.source_type

    // Determine the URL to display/link
    let sourceUrl = null
    let sourceUri = null

    if (metadata.sourceUrl) {
        sourceUrl = metadata.sourceUrl
    } else if (metadata['x-amz-bedrock-kb-source-uri']) {
        sourceUri = metadata['x-amz-bedrock-kb-source-uri']
    } else if (source.location?.url) {
        sourceUrl = source.location.url
    }

    // Use light blue background for knowledge_base sources to indicate higher relevance
    const bgColor = sourceType === 'knowledge_base' ? 'bg-blue-50' : 'bg-gray-50'

    return (
        <li className={classNames("text-xs rounded p-2", bgColor)}>
            <div className="font-medium text-gray-900 mb-1">
                {title}
            </div>
            {sourceUrl ? (
                <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 underline break-all"
                >
                    {sourceUrl}
                </a>
            ) : sourceUri ? (
                <div className="text-gray-600 break-all font-mono text-xs">
                    {sourceUri}
                </div>
            ) : null}
            {metadata.source && (
                <div className="text-gray-500 mt-1">
                    Type: {metadata.source}
                </div>
            )}
        </li>
    )
}

// Assistant Loading Bubble Component
function AssistantLoadingBubble() {
    return (
        <div className="flex justify-start">
            <div className="max-w-3xl rounded-lg px-4 py-3 shadow-sm bg-white text-gray-900">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500">
                        Assistant
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                </div>
            </div>
        </div>
    )
}

// Conversation Input Component
function ConversationInput({message, setMessage, onSend, disabled, mode, setMode}) {
    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey && !disabled) {
            e.preventDefault()
            onSend()
        }
    }

    return (
        <div className="border-t border-gray-200 bg-white p-4">
            <div className="max-w-4xl mx-auto space-y-3">
                {/* Mode Toggle */}
                <div className="flex gap-2">
                    <div className="flex-1 relative group">
                        <button
                            type="button"
                            onClick={() => setMode('RETRIEVAL_ONLY')}
                            className={classNames(
                                "w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                                mode === 'RETRIEVAL_ONLY'
                                    ? "bg-indigo-600 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            )}
                        >
                            Retrieval Only
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                            Search only within your knowledge base
                        </div>
                    </div>
                    <div className="flex-1 relative group">
                        <button
                            type="button"
                            onClick={() => setMode('PROBLEM_SOLVING')}
                            className={classNames(
                                "w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                                mode === 'PROBLEM_SOLVING'
                                    ? "bg-indigo-600 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            )}
                        >
                            Problem Solving
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                            Also uses web search & general knowledge to find solutions beyond your knowledge base, but will prioritise internal sources.
                        </div>
                    </div>
                </div>

                {/* Message Input */}
                <div className="flex gap-3">
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={disabled ? "Waiting for assistant..." : "Type your message... (Press Enter to send, Shift+Enter for new line)"}
                        rows={3}
                        disabled={disabled}
                        className={classNames(
                            "flex-1 resize-none rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent",
                            disabled && "bg-gray-100 cursor-not-allowed"
                        )}
                    />
                    <button
                        onClick={onSend}
                        disabled={!message.trim() || disabled}
                        className={classNames(
                            "px-6 py-3 rounded-lg font-medium transition-colors",
                            message.trim() && !disabled
                                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        )}
                    >
                        {disabled ? "Sending..." : "Send"}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Empty State Component
function EmptyState({onNewConversation}) {
    return (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
                <ChatBubbleLeftRightIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No conversation selected</h3>
                <p className="text-gray-500 mb-6">Select a conversation from the sidebar or start a new one</p>
                <PrimaryButton
                    label="New Conversation"
                    onClick={onNewConversation}
                    icon={<PlusIcon className="h-5 w-5 mr-2" />}
                />
            </div>
        </div>
    )
}
